/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import { Button } from 'ui/components/common';
import { useAppWrapperContext } from 'ui/layouts/AppWrapper';
import { NOTIFICATION_TYPE } from 'utils/constants';
import {
  cn,
  convertCamelCaseToCapitalize,
  useAdmin,
  useAdminParams,
} from 'utils/helpers';
import { Notification, TICKET_STATUS } from 'utils/types';

const SupportDescription = {
  TICKET_STATUS_UPDATED: (_n?: Notification) => {
    const statusColor = () => {
      switch (_n?.meta?.status) {
        case TICKET_STATUS.CLOSED:
          return 'text-primary-base';
        case TICKET_STATUS.IN_PROGRESS:
          return 'text-info-base';
        default:
          return 'text-secondary-base';
      }
    };
    const isClosed = _n?.meta?.status === TICKET_STATUS.CLOSED;
    const message = isClosed ? (
      <span>
        Ticket update: Your help ticket has been marked as{' '}
        <span className={cn(statusColor())}>Closed</span>
      </span>
    ) : (
      <span>
        Ticket update: Your help ticket has been marked{' '}
        <span className={cn(statusColor())}>
          {convertCamelCaseToCapitalize(_n?.meta?.status ?? '')}
        </span>
      </span>
    );

    return message;
  },
  TICKET_COMMENT_ADDED: (_n?: Notification) => {
    const message =
      _n?.type === NOTIFICATION_TYPE.ADMIN
        ? 'User Reply: You’ve received a new comment on a help ticket'
        : 'Support Update: You’ve received a new reply to your help ticket';

    return message;
  },
  TICKET_CREATED: (_n?: Notification) => {
    return `New Help Ticket Submitted`;
  },
  WEBSITE_BUILDER: (_n?: Notification) => {
    const meta = _n?.meta;

    return `Website Builder | ${meta?.eventType} of Duda Account`;
  },
};

const SupportLogs = {
  TICKET_STATUS_UPDATED: {
    title: 'Ticket Status Updated',
    description: (_n?: Notification) =>
      SupportDescription.TICKET_STATUS_UPDATED(_n),
    element: (notification: Notification) => {
      const navigate = useNavigate();
      const { accountId, accountUserId } = useAdminParams();
      const { isAdmin } = useAdmin();
      const {
        state: { isPreview },
      } = useAppWrapperContext();

      const link = useMemo(() => {
        if (!isAdmin && !isPreview)
          return `/help?ticketId=${notification.referenceId}`;
        if (isAdmin && !isPreview)
          return `/admin/help?ticketId=${notification.referenceId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/help/?ticketId=${notification.referenceId}`;
      }, [
        isAdmin,
        isPreview,
        notification.referenceId,
        accountId,
        accountUserId,
      ]);

      const ticket = useMemo(
        () =>
          notification.meta?._doc?.subject ?? notification.meta?.subject ?? '',
        [notification.meta],
      );

      return (
        <>
          <div className="text-title font-semibold text-sm line-clamp-2">
            {SupportDescription['TICKET_STATUS_UPDATED'](notification)}
          </div>
          <div className="flex font-normal space-x-2 text-sm line-clamp-2">
            <span>Ticket Subject:</span>
            <Button
              intent="link"
              onClick={() => navigate(link)}
              label={ticket}
              className="p-0 m-0"
            />
          </div>
        </>
      );
    },
  },
  TICKET_COMMENT_ADDED: {
    title: 'Ticket Comment Added',
    description: (_n?: Notification) =>
      SupportDescription.TICKET_COMMENT_ADDED(_n),
    element: (notification: Notification) => {
      const navigate = useNavigate();
      const { accountId, accountUserId } = useAdminParams();
      const { isAdmin } = useAdmin();
      const {
        state: { isPreview },
      } = useAppWrapperContext();

      const link = useMemo(() => {
        if (!isAdmin && !isPreview)
          return `/help?ticketId=${notification.referenceId}`;
        if (isAdmin && !isPreview)
          return `/admin/help?ticketId=${notification.referenceId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/help/?ticketId=${notification.referenceId}`;
      }, [
        isAdmin,
        isPreview,
        notification.referenceId,
        accountId,
        accountUserId,
      ]);

      const ticket = useMemo(
        () =>
          notification.meta?._doc?.subject ?? notification.meta?.subject ?? '',
        [notification.meta],
      );

      return (
        <>
          <div className="text-title font-semibold text-sm line-clamp-2">
            {SupportDescription['TICKET_COMMENT_ADDED'](notification)}
          </div>
          <div className="flex font-normal space-x-2 text-sm line-clamp-2">
            <span>Ticket Subject:</span>
            <Button
              intent="link"
              onClick={() => navigate(link)}
              label={ticket}
              className="p-0 m-0"
            />
          </div>
        </>
      );
    },
  },
  TICKET_CREATED: {
    title: 'Ticket Created',
    description: (_n?: Notification) => SupportDescription.TICKET_CREATED(_n),
    element: (notification: Notification) => {
      const navigate = useNavigate();
      const { accountId, accountUserId } = useAdminParams();
      const { isAdmin } = useAdmin();
      const {
        state: { isPreview },
      } = useAppWrapperContext();

      const link = useMemo(() => {
        if (!isAdmin && !isPreview)
          return `/help?ticketId=${notification.referenceId}`;
        if (isAdmin && !isPreview)
          return `/admin/help?ticketId=${notification.referenceId}`;

        return `/preview/accounts/${accountId}/users/${accountUserId}/help/?ticketId=${notification.referenceId}`;
      }, [
        isAdmin,
        isPreview,
        notification.referenceId,
        accountId,
        accountUserId,
      ]);

      const ticket = useMemo(
        () =>
          notification.meta?._doc?.subject ?? notification.meta?.subject ?? '',
        [notification.meta],
      );

      return (
        <>
          <div className="text-title font-semibold text-sm line-clamp-2">
            {SupportDescription['TICKET_CREATED'](notification)}
          </div>
          <div className="flex font-normal space-x-2 text-sm line-clamp-2">
            <span>Ticket Subject:</span>
            <Button
              intent="link"
              onClick={() => navigate(link)}
              label={ticket}
              className="p-0 m-0"
            />
          </div>
        </>
      );
    },
  },
  WEBSITE_BUILDER: {
    title: 'Support Action',
    description: (_n?: Notification) => SupportDescription.WEBSITE_BUILDER(_n),
    element: (notification: Notification) => {
      return (
        <>
          <div className="text-title font-semibold text-sm line-clamp-2">
            {SupportDescription['WEBSITE_BUILDER'](notification)}
          </div>
          <div className="flex font-normal space-x-2 text-sm line-clamp-2">
            Duda account of{' '}
            {notification?.meta?.account?.accountUser?.emailAddress} is due for{' '}
            {notification?.meta?.eventType}
          </div>
        </>
      );
    },
  },
};

export default SupportLogs;
